/**
 * `/klienci/$clientId` — the client detail.
 *
 * A wiring declaration, and the one place in the app where two `pages/` slices meet. FSD forbids a
 * page importing another page; `app` is the composition root and may import from every layer, which
 * is why the client's calendar is composed HERE and handed to the shell rather than imported by it.
 *
 * ── The plan calendar LEFT this screen on 19 September 2026 ───────────────────────────────────
 * Owner: the Kalendarz tab is the client's WHOLE calendar, and the plan calendar is a view of one
 * plan with a screen of its own at `/klienci/$clientId/plan/$assignmentId`. This reverses the
 * 13 August 2026 directive that put both calendars under one tab, and the paragraph this file used
 * to carry arguing against a redirect. `renderCalendar` is now one screen and `?plan=` is not a
 * parameter of this route at all — see `beforeLoad` for what happens to a link that still names it.
 *
 * ── `?tab=` uses mobile's own six keys ────────────────────────────────────────────────────────
 * So a deep link written on either platform opens the same tab (handoff B1).
 *
 * ── `?exec=` names a session, and the OVERLAY draws it ────────────────────────────────────────
 * `?exec=` is a workout execution id and `?comment=` puts focus in its comment composer rather than
 * on the panel's heading. One surface answers it here — KANON view 05's overlay, built from
 * `GET /workout-execution/{id}/display` alone. Owner, 12 August 2026: clicking a session opens the
 * session, it does not move the trainer somewhere else. The plan grid's own panel answers the same
 * parameter on the plan screen, where it has the plan slot to draw a prescription with.
 *
 * The overlay is composed here rather than imported by the detail for the reason above:
 * `pages/client-detail` may not import `pages/assigned-plan-calendar`, and `app/` may.
 *
 * ── Every navigation from here builds its search from VALIDATED values ────────────────────────
 * Never `{...current, tab}`. TanStack's `applySearchMiddleware` spreads the validator's output OVER
 * the updater's result rather than replacing it, so an unknown key a link arrived with would
 * survive every tab click and be re-serialised into the URL forever.
 *
 * ── `?q=`, `?f=` and `?s=` carry the LIST this client was opened from ─────────────────────────
 * The roster's search, filter tab and ordering are all client-side, so "the next client" only means
 * something if the list is in the address. Those three keys are it — `entities/client`'s
 * `rosterContext` owns their spelling and the rule that a default is never written, which is what
 * lets a bare `/klienci/12` still resolve to a real list rather than to two dead arrows. They ride
 * along on every navigation from this screen, including the walk to the next client: stepping down
 * a filtered list must not silently drop the filter on the first step.
 */

import {
  createFileRoute,
  redirect,
  useCanGoBack,
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from '@tanstack/react-router';

import {
  rosterContextOf,
  rosterContextSearch,
  type RosterContextSearch,
} from '~/entities/client';
import { SessionOverlay } from '~/pages/assigned-plan-calendar';
import {
  ClientCalendarScreen,
  useShowInPlan,
  type ShowInPlanTarget,
} from '~/pages/client-calendar';
import {
  ClientDetailScreen,
  DEFAULT_CLIENT_DETAIL_TAB,
  isClientDetailTab,
  type ClientDetailTab,
} from '~/pages/client-detail';
import { preloadBookingPolicy } from '~/features/credit-ledger';
import { queryClient } from '~/shared/lib/queryClient';
import { sessionOpenerKey } from '~/shared/lib/sessionOpener';

interface ClientDetailSearch extends RosterContextSearch {
  tab?: ClientDetailTab;
  /** A workout execution to open the session overlay on. */
  exec?: number;
  /** Put focus in the session's comment composer rather than on the panel heading. */
  comment?: true;
  /** A recording to open the full-screen clip player on. */
  clip?: number;
  /** A survey occurrence to open Ankiety on — a survey task's „Przejrzyj". Web-only. */
  survey?: number;
}

/** A positive integer, or `undefined`. `?exec=` is a row id and nothing else. */
function positiveInt(raw: unknown): number | undefined {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

export const Route = createFileRoute('/_shell/klienci/$clientId')({
  /**
   * **Old addresses keep working.** `/klienci/1?tab=calendar&plan=45` and `/klienci/1?plan=45` are
   * what every link written before 19 September 2026 says — the summary column's plan card, the
   * roster's preview panel, a trainer's own bookmark — and both now mean the plan's own screen.
   *
   * It runs in `beforeLoad` and `throw`s, so nothing of this screen mounts first: a redirect taken
   * in the component would paint the detail, fire its header read and then navigate away.
   * `replace: true` puts NO entry in the history, so Back from the plan screen goes wherever the
   * trainer was before the old link rather than to the link itself, which would bounce them
   * forward again on every press.
   *
   * The raw search is read rather than the validated one: `?plan=` is no longer part of this
   * route's schema, so the validator has already dropped it by the time a loader would see it.
   * `?exec=`, `?comment=` and the roster context are carried across — a link that opened a session
   * still opens that session, on the surface that now owns it.
   */
  beforeLoad: ({ params, search }) => {
    const raw = search as Record<string, unknown>;
    const plan = positiveInt(raw.plan);
    if (plan === undefined) return;
    const exec = positiveInt(raw.exec);
    redirect({
      to: '/klienci/$clientId/plan/$assignmentId',
      params: { clientId: params.clientId, assignmentId: String(plan) },
      search: {
        ...rosterContextSearch(rosterContextOf(raw)),
        ...(exec === undefined ? {} : { exec }),
        ...(exec !== undefined && (raw.comment === true || raw.comment === 'true')
          ? { comment: true as const }
          : {}),
      },
      replace: true,
      throw: true,
    });
  },
  validateSearch: (raw: Record<string, unknown>): ClientDetailSearch => {
    const exec = positiveInt(raw.exec);
    const clip = positiveInt(raw.clip);
    const survey = positiveInt(raw.survey);
    // An unrecognised `?tab=` is DROPPED rather than defaulted into the URL: the router would
    // otherwise rewrite a link the trainer pasted, and the screen falls back on its own below.
    const tab = isClientDetailTab(raw.tab) ? raw.tab : undefined;
    // Normalised through the same reader the screen uses, then re-serialised with the defaults
    // dropped — so `?f=all` and `?s=nonsense` are removed from the address rather than kept as
    // noise that means nothing, and what stays is exactly what changes the list.
    const list = rosterContextSearch(rosterContextOf(raw));
    return {
      ...list,
      // Omitted rather than set to `undefined`, so the router does not serialise a bare `?tab=`.
      ...(tab === undefined ? {} : { tab }),
      ...(exec === undefined ? {} : { exec }),
      ...(clip === undefined ? {} : { clip }),
      // Only Ankiety reads it, so it is dropped on every other tab rather than left as noise.
      ...(survey === undefined || tab !== 'surveys' ? {} : { survey }),
      // Meaningless without a session to focus the composer of, so it is dropped with it rather
      // than left in the URL as a flag pointing at nothing.
      ...(exec !== undefined && (raw.comment === true || raw.comment === 'true')
        ? { comment: true as const }
        : {}),
    };
  },
  // Whether credits run, before the first frame: the Przegląd tab's „Rozliczenie" band holds its
  // room on it, so a cold entry does not move when the credit summary lands.
  loader: () => preloadBookingPolicy(queryClient),
  component: ClientDetailRoute,
});

function ClientDetailRoute() {
  const { clientId } = useParams({ from: '/_shell/klienci/$clientId' });
  const search = useSearch({ from: '/_shell/klienci/$clientId' });
  const { tab, exec, comment, clip, survey } = search;
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  /**
   * The list this client was opened from, in both shapes: `listContext` is what the screen walks,
   * `list` is what every navigation from here has to re-write.
   *
   * It has to be re-written explicitly on each one. A `search` updater REPLACES the search object,
   * so an omitted key is a dropped key — and dropping these three mid-walk would put the trainer on
   * the unfiltered roster's neighbour after one press, silently.
   */
  const listContext = rosterContextOf(search);
  const list = rosterContextSearch(listContext);

  // A route parameter is a string and not necessarily a number at all. `null` short-circuits every
  // read rather than firing requests that can only 404.
  const id = Number(clientId);
  const clientIdOrNull = Number.isInteger(id) && id > 0 ? id : null;

  /**
   * The walk from a session to its plan, wired here because it crosses a seam no page may cross.
   *
   * `pages/client-calendar` owns the join (a calendar event names no plan, so it is searched for);
   * `pages/assigned-plan-calendar` owns the overlay that draws the control; `entities/client` owns
   * the address. This route is the only place all three are legal, which is the same reason the
   * calendar itself is composed here.
   *
   * Enabled only while a session overlay is open — the join is one request per plan the client has
   * ever been on, and a trainer reading Pomiary should not pay for it.
   */
  const resolveShowInPlan = useShowInPlan(clientIdOrNull, exec !== undefined);

  const goToPlan = (target: ShowInPlanTarget) => {
    // PUSHES: leaving the client for one of their plans is navigation the trainer took, and Back is
    // the way home. `?exec=` rides so the plan's own panel opens on the session they pressed.
    void navigate({
      to: '/klienci/$clientId/plan/$assignmentId',
      params: { clientId: String(target.clientId), assignmentId: String(target.assignmentId) },
      search: { ...list, exec: target.workoutExecutionId },
    });
  };

  // `isClientDetailTab` runs AGAIN here, not only in `validateSearch`: the router merges the raw
  // search UNDER the validator's output (the same `applySearchMiddleware` spread the header above
  // documents for writes), so a key the validator DROPPED still arrives — `?tab=garbage` reached
  // this line as the literal string and fell through the panel chain to its final else, drawing
  // the Ankiety placeholder with no tab selected. Measured in the running app, 12 Aug 2026.
  const activeTab = isClientDetailTab(tab) ? tab : DEFAULT_CLIENT_DETAIL_TAB;

  return (
    <ClientDetailScreen
      clientId={clientIdOrNull}
      tab={activeTab}
      onSelectTab={(next) => {
        // PUSHES rather than replaces: moving between tabs is navigation the trainer took, and Back
        // returning to the tab they came from is what a browser is for.
        //
        // `?exec=` deliberately does not ride along: it names a session the trainer asked for once,
        // and clicking a tab by hand is not that request being made again. Leaving it would reopen
        // the overlay every time they came back.
        void navigate({
          to: '.',
          search: () => ({ ...list, tab: next }),
        });
      }}
      onOpenSession={(workoutExecutionId, intent) => {
        // The tab the trainer is on is KEPT. Owner, 12 August 2026: opening a session from the
        // activity timeline opens the session; it does not take the trainer somewhere else and
        // leave them to work out what happened. It PUSHES, so Back closes the overlay — the one
        // browser affordance a panel that lives in the URL gets for free.
        void navigate({
          to: '.',
          search: () => ({
            ...list,
            tab: activeTab,
            exec: workoutExecutionId,
            ...(intent === 'comment' ? { comment: true as const } : {}),
          }),
        });
      }}
      sessionOverlay={
        exec === undefined
          ? null
          : {
              // The control this was opened from, so closing it can put focus back. The intent is
              // half the address — `?comment=` names the CTA, its absence names the activity row —
              // and both controls stamp the same key on themselves.
              openerKey: sessionOpenerKey(exec, comment === true ? 'comment' : 'read'),
              node: (
                <SessionOverlay
                  // A new session is a new overlay: remounting is what re-runs the read, resets the
                  // scroll and starts the comment draft clean.
                  key={exec}
                  workoutExecutionId={exec}
                  focusComment={comment === true}
                  /* The same control the day preview's footer draws, in the one host that is not
                   * already inside a plan. `null` where the session's plan could not be resolved —
                   * a deleted plan, or an occurrence older than day slot tokens. */
                  onShowInPlan={(() => {
                    const target = resolveShowInPlan(exec);
                    return target === null ? null : () => { goToPlan(target); };
                  })()}
                  onClose={() => {
                    // REPLACES: the open pushed an entry, so replacing it on close means Back goes
                    // to where the trainer was before they opened the session rather than reopening
                    // it. `?exec=` and `?comment=` both go — the URL says what is on screen.
                    void navigate({
                      to: '.',
                      search: () => ({ ...list, tab: activeTab }),
                      replace: true,
                    });
                  }}
                />
              ),
            }
      }
      clipId={clip ?? null}
      onShowClip={(clipId, history) => {
        // CLOSING goes back to wherever the player was opened from (owner, 25 September 2026): the
        // Pulpit, `/zadania`, this client's Przegląd or Forma. Every in-app way into the player
        // PUSHED its entry — a queue card's link, a tab's `push` above — and switching clips inside
        // it replaces, so the entry behind the player is always the origin, and Back is the one
        // router primitive that returns there with its own state. `canGoBack` is TanStack's
        // "this tab has an entry of ours behind this one": `false` on a link opened cold, where
        // there is nowhere in the app to return to, and the player closes onto this client's tab.
        if (clipId === null && canGoBack) {
          router.history.back();
          return;
        }
        void navigate({
          to: '.',
          search: () => ({ ...list, tab: activeTab, ...(clipId === null ? {} : { clip: clipId }) }),
          replace: history === 'replace',
        });
      }}
      surveyId={survey ?? null}
      onOpenSurvey={(surveyAssignmentId) => {
        // PUSHES, as a tab press does: Back returns to the Przegląd card it was opened from.
        void navigate({
          to: '.',
          search: () => ({ ...list, tab: 'surveys' as const, survey: surveyAssignmentId }),
        });
      }}
      onBack={() => {
        void navigate({ to: '/klienci' });
      }}
      listContext={listContext}
      onOpenClient={(nextClientId) => {
        // The LIST rides along and so does the TAB: a trainer comparing measurements down a
        // filtered list wants the next person's measurements, not their Przegląd. What is dropped
        // is everything that belonged to the client being left — `?exec=` names one of THIS
        // client's sessions, and it does not exist on the next person's screen.
        //
        // PUSHES, so Back walks the list backwards. That is the same affordance the arrows give,
        // reached the way a browser user expects to reach it.
        void navigate({
          to: '/klienci/$clientId',
          params: { clientId: String(nextClientId) },
          search: () => ({ ...list, ...(tab === undefined ? {} : { tab }) }),
        });
      }}
      renderCalendar={() => (
        /* The client's WHOLE calendar, and only that (owner, 19 September 2026). The plan grid used
         * to share this tab behind `?plan=`; it has a screen of its own now, and this tab carries
         * no link to it — the plan is one click from Przegląd and the tab is not its shortcut. */
        <ClientCalendarScreen
          clientId={clientIdOrNull}
          openExecutionId={exec ?? null}
          onOpenSession={(workoutExecutionId) => {
            // The same push the Przegląd timeline's rows make — `?exec=` on the tab the trainer is
            // already on, so Back closes the overlay.
            void navigate({
              to: '.',
              search: () => ({ ...list, tab: activeTab, exec: workoutExecutionId }),
            });
          }}
          onShowInPlan={goToPlan}
        />
      )}
    />
  );
}
